import { trace } from 'owa-trace';
import type { AccountKey, MailboxType } from 'owa-client-types';
import type {
    MailboxCollectorAccountInfo,
    MailboxAccountInfoMappingAddon,
    MailboxCollectorUrlMapping,
} from 'owa-analytics-types';
import { CACHE_KEY_PREFIX, GLOBAL_ACCOUNT_CACHE_KEY } from 'owa-analytics-types';
import { sendConfigToWorkers } from 'owa-worker-config-sync-send';

type MailboxAccountInfoMappingEntry = MailboxCollectorAccountInfo & {
    collectorUrl: string;
};

const mailboxCollectorMapping = new Map<
    AccountKey | 'globalAccount',
    MailboxAccountInfoMappingEntry
>();
const accountKeyLogged = new Set<AccountKey | 'globalAccount'>();

function getCollectorUrlForMailbox(accountKey: AccountKey | 'globalAccount'): string | undefined {
    if (!accountKeyLogged.has(accountKey)) {
        trace.info(
            `[CollectorUrl] Getting collectorUrl mapping for accountKey: ${accountKey}`,
            'analytics'
        );
        accountKeyLogged.add(accountKey);
    }
    return mailboxCollectorMapping.get(accountKey)?.collectorUrl;
}

function getMappedMailboxAccountInfo(
    accountKey: AccountKey | 'globalAccount'
): MailboxCollectorAccountInfo | undefined {
    const item = mailboxCollectorMapping.get(accountKey);
    if (!item) {
        return undefined;
    }

    return {
        isGlobalAccount: item.isGlobalAccount,
        multiAccountType: item.multiAccountType,
        mailboxType: item.mailboxType,
    };
}

function getMailboxCacheKey(
    accountKey: AccountKey | 'globalAccount' | undefined,
    scenario: string
): string {
    // Currently a warning but it should be upgraded to an error once MailboxInfo is fully enforced.
    if (!accountKey) {
        trace.warn(
            `[MailboxCacheKey] No accountKey provided for scenario: ${scenario}. Defaulting to global cache key. This may cause cache collisions if multiple secondary accounts are used without proper mapping. accountKey=${accountKey}`,
            'analytics'
        );
        return GLOBAL_ACCOUNT_CACHE_KEY;
    }

    const info = mailboxCollectorMapping.get(accountKey);
    // Map global accounts to a shared cache key.
    if (info?.isGlobalAccount || accountKey === 'globalAccount') {
        return GLOBAL_ACCOUNT_CACHE_KEY;
    }

    // Other accounts should use their own cache key
    return `${CACHE_KEY_PREFIX}${accountKey}`;
}

function setMailboxCollectorUrlMapping(
    accountKey: AccountKey | 'globalAccount',
    collectorUrl: string,
    multiAccountType: 'GlobalAccount' | 'SecondaryAccount',
    mailboxType: MailboxType | undefined,
    alternateAccountKey?: AccountKey
) {
    trace.info(
        `[CollectorUrl] Setting collectorUrl mapping for accountKey: ${accountKey}, collectorUrl: ${collectorUrl}`,
        'analytics'
    );
    const entry: MailboxAccountInfoMappingEntry = {
        collectorUrl,
        multiAccountType,
        mailboxType,
        isGlobalAccount: multiAccountType === 'GlobalAccount',
    };

    mailboxCollectorMapping.set(accountKey, entry);

    if (alternateAccountKey && alternateAccountKey !== accountKey) {
        trace.info(
            `[CollectorUrl] Setting alternate collectorUrl mapping for accountKey: ${alternateAccountKey}, collectorUrl: ${collectorUrl}`,
            'analytics'
        );
        mailboxCollectorMapping.set(alternateAccountKey, entry);
    }
}

function syncAllMailboxAccountInfoMappings(): void {
    trace.info(
        `[CollectorUrl] Syncing all mailbox collector URL mappings to workers. Total mappings: ${mailboxCollectorMapping.size}`,
        'analytics'
    );
    for (const [accountKey, entry] of mailboxCollectorMapping) {
        sendConfigToWorkers('all', 'mailboxCollectorMapping', {
            accountKey,
            collectorUrl: entry.collectorUrl,
            multiAccountType: entry.multiAccountType,
            mailboxType: entry.mailboxType,
        });
    }
}

function getAllMailboxCollectorUrlMappings(): MailboxCollectorUrlMapping[] {
    return Array.from(mailboxCollectorMapping.entries()).map(([accountKey, entry]) => ({
        accountKey,
        collectorUrl: entry.collectorUrl,
        multiAccountType: entry.multiAccountType,
        mailboxType: entry.mailboxType,
    }));
}

export function mailboxCollectorMappingAddon(): MailboxAccountInfoMappingAddon {
    return {
        getCollectorUrlForMailbox,
        getMappedMailboxAccountInfo,
        setMailboxCollectorUrlMapping,
        syncAllMailboxAccountInfoMappings,
        getAllMailboxCollectorUrlMappings,
        getMailboxCacheKey,
    };
}
